import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { PersonnelProject, TMTimesheet } from "@/hooks/usePersonnelProjects";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  draft: { label: "Draft", className: "" },
  sent: { label: "Sent", className: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" },
  approved: { label: "Approved", className: "bg-green-100 text-green-700" },
  paid: { label: "Paid", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300" },
  partially_paid: { label: "Partial", className: "bg-amber-100 text-amber-700" },
  overdue: { label: "Overdue", className: "bg-red-100 text-red-700" },
};

interface Props {
  project: PersonnelProject;
  timesheets: TMTimesheet[];
}

export function PersonnelInvoicesTab({ project, timesheets }: Props) {
  const isMobile = useIsMobile();
  const { user } = useAuth();

  // Query real invoices
  const { data: invoices = [] } = useQuery({
    queryKey: ["personnel_invoices", project.id],
    queryFn: async () => {
      const projectId = (project as any).project_id ?? project.id;
      const { data, error } = await supabase
        .from("invoices")
        .select("*, invoice_lines(description, quantity, unit_price, line_total)")
        .eq("project_id", projectId)
        .eq("invoice_type", "personnel")
        .is("deleted_at", null)
        .order("invoice_date", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });

  // Fallback: show old derived invoices if no real ones exist
  const invoicedSheets = timesheets.filter((t) => t.status === "invoiced" || t.status === "paid");
  const showRealInvoices = invoices.length > 0;
  const displayData = showRealInvoices ? invoices : [];

  return (
    <div className="space-y-4 mt-4">
      <h2 className="text-base font-semibold">Customer Invoices ({showRealInvoices ? invoices.length : invoicedSheets.length})</h2>

      {showRealInvoices ? (
        /* Real invoices from invoices table */
        invoices.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">No invoices generated yet</p>
        ) : isMobile ? (
          <div className="space-y-3">
            {invoices.map((inv: any) => {
              const badge = STATUS_BADGE[inv.status] ?? STATUS_BADGE.draft;
              return (
                <Card key={inv.id}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-medium">{inv.invoice_number}</span>
                      <Badge variant="outline" className={badge.className}>{badge.label}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{inv.notes || format(new Date(inv.invoice_date), "MMM d, yyyy")}</p>
                    {inv.invoice_lines?.map((l: any, i: number) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-muted-foreground">{l.description}</span>
                        <span className="font-medium">{fmt(l.line_total)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t pt-1">
                      <span className="text-sm font-semibold">Total</span>
                      <span className="text-lg font-bold">{fmt(Number(inv.total ?? 0))}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Lines</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv: any) => {
                    const badge = STATUS_BADGE[inv.status] ?? STATUS_BADGE.draft;
                    return (
                      <TableRow key={inv.id}>
                        <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{inv.notes || format(new Date(inv.invoice_date), "MMM d, yyyy")}</TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            {inv.invoice_lines?.map((l: any, i: number) => (
                              <div key={i} className="text-xs text-muted-foreground">
                                {l.description}: {l.quantity}h × {fmt(l.unit_price)} = {fmt(l.line_total)}
                              </div>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{fmt(Number(inv.total ?? 0))}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={badge.className}>{badge.label}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )
      ) : (
        /* Fallback: derived from timesheet status (backward compat) */
        invoicedSheets.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">No invoices generated yet. Approve a timesheet to generate invoices.</p>
        ) : isMobile ? (
          <div className="space-y-3">
            {invoicedSheets.map((t, i) => (
              <Card key={t.id}>
                <CardContent className="p-4 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-medium">{project.project_number}-W{String(i + 1).padStart(2, "0")}</span>
                    <Badge variant="outline" className={t.status === "paid" ? "bg-emerald-500/10 text-emerald-700" : ""}>
                      {t.status}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{format(new Date(t.week_ending), "MMM d, yyyy")}</span>
                    <span className="text-lg font-bold">{fmt(t.total_billed)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Week Ending</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoicedSheets.map((t, i) => (
                    <TableRow key={t.id}>
                      <TableCell className="font-mono">{project.project_number}-W{String(i + 1).padStart(2, "0")}</TableCell>
                      <TableCell>{format(new Date(t.week_ending), "MMM d, yyyy")}</TableCell>
                      <TableCell className="text-right">{fmt(t.total_billed)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={t.status === "paid" ? "bg-emerald-500/10 text-emerald-700" : ""}>
                          {t.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )
      )}
    </div>
  );
}
